import { useCallback, useEffect, useState } from 'react';
import useFetch, { CachePolicies } from 'use-http';
import { useUnit } from 'effector-react';
import { $user } from 'models/user';
import { $store } from 'models/store';
import { TableFilter } from 'components/UI/Table';
import { IBreach } from 'types/breach';
import { buildQueryParams } from '../tableUtils';
import { determineStoreAccess, sortDataByStoreNumber } from '../dataUtils';
import { PER_PAGE } from '../constants';

interface UseBreachDataOptions {
	typeFilter?: string; // например, 'PhotoError' для фото нарушений
}

interface LoadResult {
	rows: IBreach[];
	count: number;
}

export const useBreachData = (filter: TableFilter, offset: number, options: UseBreachDataOptions = {}) => {
	const { get } = useFetch({ cachePolicy: CachePolicies.NO_CACHE });
	const user = useUnit($user);
	const store = useUnit($store);

	const [data, setData] = useState<IBreach[]>([]);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<Error | null>(null);

	const load = useCallback(async (): Promise<LoadResult> => {
		setLoading(true);
		setError(null);

		try {
			const params = buildQueryParams(filter);
			const currentParams = new URLSearchParams(params);

			let url = `/api/breach?offset=${offset}&limit=${PER_PAGE}`;

			if (options.typeFilter) {
				url += `&type=${options.typeFilter}`;
			}

			const storeId = determineStoreAccess(user, store);

			if (storeId !== null) {
				if (storeId) {
					currentParams.set('storeId', storeId);
				}
				url += `&${currentParams.toString()}`;

				const result = await get(url);

				if (result?.rows) {
					const sortedData = sortDataByStoreNumber(result.rows);
					setData(sortedData);
					return { rows: sortedData, count: result.count };
				}
			}

			setData([]);
			return { rows: [], count: 0 };
		} catch (err) {
			setError(err instanceof Error ? err : new Error('Unknown error'));
			setData([]);
			return { rows: [], count: 0 };
		} finally {
			setLoading(false);
		}
	}, [filter, offset, user, store, get, options.typeFilter]);

	return {
		data,
		loading,
		error,
		load,
	};
};
